import React, { useEffect, useState, useRef } from 'react';
import { api } from '../api';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ShareButton from './ShareButton';

function SubmittedMovies() {
  const location = useLocation();
  const navigate = useNavigate();
  const { participantName, partyCode } = location.state || {};
  const [submissions, setSubmissions] = useState([]);
  const [watchProviders, setWatchProviders] = useState({});
  const [shareableLink, setShareableLink] = useState('');
  const pollRef = useRef(null);
  const apiKey = process.env.REACT_APP_TMDB_API_KEY;

  useEffect(() => {
    if (partyCode) {
      const baseUrl = window.location.origin;
      const link = `${baseUrl}/link-invitation?partyCode=${partyCode}`;
      setShareableLink(link);
    }
  }, [partyCode]);

  const fetchWatchProviders = async (movieId) => {
    try {
      const response = await axios.get(
        `https://api.themoviedb.org/3/movie/${movieId}/watch/providers`,
        { params: { api_key: apiKey } }
      );
      const results = response.data.results;
      return results.US || results.GB || null;
    } catch (error) {
      console.error('Error fetching watch providers:', error);
      return null;
    }
  };

  const fetchSubmissions = async () => {
    try {
      const data = await api.getSubmissions(partyCode);

      const submissionsData = [];
      const providersData = {};

      for (const row of data) {
        try {
          const movieResponse = await axios.get(
            `https://api.themoviedb.org/3/movie/${row.movie_id}`,
            { params: { api_key: apiKey } }
          );
          const providers = await fetchWatchProviders(row.movie_id);
          providersData[row.movie_id] = providers;

          submissionsData.push({
            name: row.name,
            movie: movieResponse.data
          });
        } catch (error) {
          console.error('Error fetching movie details:', error);
        }
      }

      setSubmissions(submissionsData);
      setWatchProviders(providersData);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  };

  // Poll for winner and new submissions (replaces Supabase Realtime)
  const pollForUpdates = async () => {
    try {
      const party = await api.getParty(partyCode);
      if (party?.winner) {
        navigate('/winner', {
          state: {
            movie: party.winner.movie,
            submitter: party.winner.submitter,
            partyCode: partyCode,
            watchProviders: party.winner.watchProviders
          },
          replace: true
        });
        return;
      }
    } catch (error) {
      console.error('Error polling party:', error);
    }

    fetchSubmissions();
  };

  useEffect(() => {
    if (!partyCode || !participantName) {
      navigate('/');
      return;
    }

    // Prevent going back
    window.history.pushState(null, null, window.location.pathname);
    const handlePopState = () => {
      window.history.pushState(null, null, window.location.pathname);
    };
    window.addEventListener('popstate', handlePopState);

    // Initial fetch
    fetchSubmissions();

    // Poll every 3 seconds
    pollRef.current = setInterval(pollForUpdates, 3000);

    return () => {
      clearInterval(pollRef.current);
      window.removeEventListener('popstate', handlePopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyCode, participantName, navigate]);

  const renderWatchProviders = (movieId) => {
    const providers = watchProviders[movieId];
    if (!providers) return null;

    const allProviders = new Map();
    ['flatrate', 'rent', 'buy'].forEach(type => {
      if (providers[type]) {
        providers[type].forEach(provider => {
          allProviders.set(provider.provider_id, provider);
        });
      }
    });

    if (allProviders.size === 0) return null;

    return (
      <div className="watch-providers">
        <div className="provider-section">
          <h4>Watch on:</h4>
          <div className="provider-logos">
            {Array.from(allProviders.values()).map(provider => (
              <img
                key={provider.provider_id}
                src={`https://image.tmdb.org/t/p/original${provider.logo_path}`}
                alt={provider.provider_name}
                title={provider.provider_name}
                className="provider-logo"
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="submitted-movies-container">
      <div className="header-section">
        <h1>Submitted Movies</h1>
        <p>View all submitted movies for this session</p>
      </div>
      
      <div className="submissions-list">
        {submissions.map((submission, index) => (
          <div key={index} className="submission-card">
            <div className="submission-content">
              <img 
                src={submission.movie.poster_path 
                  ? `https://image.tmdb.org/t/p/w200${submission.movie.poster_path}` 
                  : '/na.png'
                } 
                alt={submission.movie.title}
                className="movie-poster"
              />
              <div className="movie-info">
                <h3>
                  {submission.movie.title} ({submission.movie.release_date?.split('-')[0] || 'N/A'})
                </h3>
                <p className="submitter">Submitted by: {submission.name}</p>
                <p className="overview">{submission.movie.overview}</p>
              </div>
            </div>
            {renderWatchProviders(submission.movie.id)}
          </div>
        ))}
      </div>

      <ShareButton shareableLink={shareableLink} partyCode={partyCode} />

      <style jsx>{`
        .submitted-movies-container {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .submissions-list {
          display: grid;
          gap: 20px;
          margin-top: 20px;
        }

        .submission-card {
          background: #ffffff;
          border-radius: 8px;
          padding: 15px;
          box-shadow: 0 2px 5px rgba(0,0,0,0.3);
          border: 1px solid #eeeeee;
        }

        .submission-content {
          display: flex;
          gap: 20px;
          margin-bottom: 15px;
        }

        .movie-poster {
          width: 130px;
          height: 195px;
          border-radius: 4px;
          object-fit: cover;
          box-shadow: 0 2px 5px rgba(0,0,0,0.3);
        }

        .movie-info {
          flex: 1;
          font-size: 18px;
        }

        .movie-info h3 {
          margin: 0 0 0px 0;
          color: #333333;
          font-size: 18px;
        }

        .submitter {
          color: #FF4040;
          margin-top: 0px;
          margin-bottom: 0px;
          font-size: 14px;
        }

        .overview {
          color: #666666;
          font-size: 12px;
          line-height: 1.4;
          margin-top: 0px;
        }

        .watch-providers {
          border-top: 1px solid #eeeeee;
          margin-top: 15px;
        }

        .provider-section h4 {
          color: #FF4040;
          margin: 5px 0;
          font-size: 0.9em;
        }

        .provider-logos {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          align-items: center;
        }

        .provider-logo {
          width: 30px;
          height: 30px;
          border-radius: 5px;
          object-fit: cover;
        }

        .provider-logo:hover {
          transform: scale(1.1);
          transition: transform 0.2s ease;
        }
      `}</style>
    </div>
  );
}

export default SubmittedMovies;
